import { action, observable } from 'mobx';
import UserManagementRepository from '@app/src/common/repository/UserManagementRepository';
import AuthStore from '@app/src/common/store/AuthStore';
import { getRoutes } from '@app/src/common/constants/routeUrls';
import routerStore from '@app/src/common/store/RouterStore';
import { DateTimeService } from '@app/src/services/DateTimeService';
import notificationService from '@app/src/services/notification-service/NotificationService';
import { DynamicForm } from '@app/src/common/components/dynamic-form';
import { signInFormConfig } from './SignInFormFields';
import MfaChallengeStore from './MfaChallengeStore';
import { resolveSignInOutcome } from './mfaErrorMapping';
import { IMfaChallenge } from '@app/src/common/model/MfaChallenge';

export default class AuthPageStore {
  @observable loading: boolean = false;
  // Active email two-factor challenge. Held only in memory, so a reload returns to the credential
  // form; nothing about the challenge is ever persisted.
  @observable.ref challenge: MfaChallengeStore | null = null;
  form = new DynamicForm(signInFormConfig);

  @action
  signIn = async (email: string, password: string) => {
    this.loading = true;
    try {
      const response = await UserManagementRepository.signIn({
        email,
        password
      });
      const outcome = resolveSignInOutcome(response.data);
      if (outcome.kind === 'challenge') {
        // No token is stored and no authenticated navigation happens until the code is verified.
        this.startChallenge(outcome.challenge);
        return;
      }
      if (outcome.kind !== 'token') {
        this.loading = false;
        notificationService.errorMessage('notification.auth.error');
        return;
      }
      await this.completeSignIn(outcome.accessToken, outcome.refreshToken);
    } catch (reject: any) {
      this.loading = false;
      if (reject.response) {
        // Defensive: the challenge is a successful outcome, but a build that delivered it as a 4xx
        // must not be mistaken for a credential failure.
        const challengeOutcome = resolveSignInOutcome(reject.response.data);
        if (challengeOutcome.kind === 'challenge') {
          this.startChallenge(challengeOutcome.challenge);
          return;
        }
        const lockExpires = reject.response.data?.Data?.LockExpires;
        if (lockExpires) {
          const lockExpiresTime = new Date(lockExpires).getTime();
          const remainingMinutes = DateTimeService.getMinutes(
            lockExpiresTime - new Date().getTime()
          );
          notificationService.warningMessage(
            'notification.auth.error.try.again.min',
            { 0: remainingMinutes.toString() }
          );
          return;
        }
        notificationService.errorMessage('notification.auth.error');
        return;
      }
      notificationService.errorMessage('notification.network.error');
    }
  };

  // Returns to the credential form. The server challenge is deliberately not cancelled, so signing
  // in again with the same client context resumes it without sending another email.
  @action
  clearChallenge = () => {
    this.challenge?.dispose();
    this.challenge = null;
  };

  @action
  private startChallenge = (challenge: IMfaChallenge) => {
    this.loading = false;
    this.challenge?.dispose();
    this.challenge = new MfaChallengeStore(challenge, this.completeSignIn);
  };

  // The one sign-in continuation, shared by password success and successful code verification, so
  // both end up with identical authentication, initialization and redirect behavior.
  private completeSignIn = async (
    accessToken: string,
    refreshToken: string
  ) => {
    AuthStore.setToken(accessToken, refreshToken);
    AuthStore.setSupportLogin(false);
    await AuthStore.initCurrentUser();
    const routes = await getRoutes();
    if (!routes) {
      return;
    }
    this.loading = false;
    routerStore.updateRoutes(routes);
    routerStore.navigate(routerStore.firstRoutePath);
  };
}
